import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {Utils} from '@astek/utils';
import {MeasureTypeEnum} from '../../../../core/services/state/measure-type.enum';
import {PressureUnitEnum} from '../../../../core/services/state/pressure-unit.enum';
import {GenderEnum} from '../../../../core/services/state/gender.enum';
import {FileParserService} from '../file-parser/file-parser.service';
import {ApiPatientResponse} from '../http/api-patient-response';
import {ApiFileResponse} from '../http/api-file-response';
import {PatientData} from './patient-data';
import {AvailablePatientOptions} from './patient/available-patient-options';
import {PatientOption} from './patient/patient-option';
import {Shunt} from './patient/shunt';
import {TimestampedComment} from './timestamped-comment';
import {Measurement} from './measurement';
import {ApiAlgoResultsResponse} from '../http/api-algo-results-response';
import {ApiBasalParametersEstimationResponse} from '../http/api-basal-parameters-response';
import {BaselineParametersResults} from './baseline-parameters-results';
import {ApiInfusionParametersEstimationResponse} from '../http/api-infusion-parameters-response';
import {InfusionParametersResults} from './infusion-parameters-results';
import {PressureRange} from './pressure-range';

@Injectable()
export class PatientDataParserService {

  private _patientData: PatientData = new PatientData();

  constructor(
    // private readonly _resultsParser: ResultsParserService,
    private readonly _fileParser: FileParserService,
  ) {
  }

  public parsePatientResponse(patient: ApiPatientResponse): PatientData {
    this._patientData = new PatientData();
    this._patientData.id = patient.id;

    if (!Utils.enumValueExists(patient.measureType, MeasureTypeEnum)) {
      throw new Error(`Unexpected measure type: ${patient.measureType}`);
    }
    this._patientData.measureType = patient.measureType as MeasureTypeEnum;
    this._patientData.needleGauge = patient.needleGauge;
    if (!Utils.enumValueExists(patient.unitType, PressureUnitEnum)) {
      throw new Error(`Unexpected pressure unit type: ${patient.unitType}`);
    }
    this._patientData.pressureUnit = patient.unitType as PressureUnitEnum;
    this._patientData.createdAt = patient.createdAt;

    this._patientData.firstName = patient.firstName;
    this._patientData.lastName = patient.lastName;
    if (!Utils.enumValueExists(patient.gender, GenderEnum)) {
      throw new Error(`Unexpected gender: ${patient.gender}`);
    }
    this._patientData.gender = patient.gender as GenderEnum;
    this._patientData.birthDate = patient.birthDate;
    this._patientData.age = patient.age;
    this._patientData.medicalId = patient.medicalId;
    this.parsePatientOptions(this._patientData.medicalHistory, patient.medicalHistory);
    this.parsePatientOptions(this._patientData.clinicalSigns, patient.clinicalSigns);
    this.parsePatientOptions(this._patientData.imaging, patient.imaging);
    if (Utils.isNotNullOrUndefined(patient.shuntModel)
      || Utils.isNotNullOrUndefined(patient.shuntBrand)
      || Utils.isNotNullOrUndefined(patient.shuntType)) {
      this._patientData.shunt = new Shunt();
      this._patientData.shunt.model = Utils.valueOr(patient.shuntModel, '');
      this._patientData.shunt.brand = Utils.valueOr(patient.shuntBrand, '');
      this._patientData.shunt.adjustment = patient.shuntAdjustment;
      this._patientData.shunt.antiSyphon = Utils.valueOr(patient.shuntAntiSyphon, false);
      this._patientData.shunt.type = Utils.valueOr(patient.shuntType, '');
    }
    this._patientData.comments = patient.comments;

    this._patientData.algoResultsId = patient.algoResultsId;
    this._patientData.infusionRate = patient.infusionRate;

    for (const comment of patient.measureComments) {
      this._patientData.timestampedComments.push(new TimestampedComment(
        comment.timestamp,
        comment.value,
      ));
    }

    return this._patientData;
  }

  public parseFileResponse(file: ApiFileResponse): Observable<PatientData> {
    return this._fileParser.parsePressureFileContent(file).pipe(
      map((measurements: Array<Measurement>) => {
        this._patientData.measurements = measurements;
        if (Utils.isNotNullOrUndefined(this._patientData.infusionResults)) {
          if (Utils.isNotNullOrUndefined(this._patientData.firstMeasurement)) {
            this._patientData.infusionResults.basal.start = this._patientData.firstMeasurement.timestamp;
            this._patientData.infusionResults.plateau.start = this._patientData.firstMeasurement.timestamp;
          }
          if (Utils.isNotNullOrUndefined(this._patientData.lastMeasurement)) {
            this._patientData.infusionResults.basal.end = this._patientData.lastMeasurement.timestamp;
            this._patientData.infusionResults.plateau.end = this._patientData.lastMeasurement.timestamp;
          }
        }
        return this._patientData;
      }),
    );
  }

  public parseAlgoResultsResponse(response: ApiAlgoResultsResponse): PatientData {
    if (response.measureType === MeasureTypeEnum.BASELINE.toString()) {
      const apiBasalResults = response.parametersResults as ApiBasalParametersEstimationResponse;
      this._patientData.results.baselineParametersResults = new BaselineParametersResults(
        new PressureRange(
          Number.NEGATIVE_INFINITY,
          Number.POSITIVE_INFINITY,
          apiBasalResults.Pb,
        ),
      );
    } else {
      const apiInfusionResults = response.parametersResults as ApiInfusionParametersEstimationResponse;
      this._patientData.results.infusionParametersResults = new InfusionParametersResults(
        apiInfusionResults.Rout,
        this._patientData.infusionRate!,
        apiInfusionResults.E,
        new PressureRange(
          Number.NEGATIVE_INFINITY,
          Number.POSITIVE_INFINITY,
          apiInfusionResults.Pb,
        ),
        new PressureRange(
          Number.NEGATIVE_INFINITY,
          Number.POSITIVE_INFINITY,
          apiInfusionResults.Pth,
        ),
      );
    }
    return this._patientData;
  }

  private parsePatientOptions(options: AvailablePatientOptions, response: Array<string> | null): void {
    options.options = [];
    for (const option of (response ?? [])) {
      options.options.push(new PatientOption(option, true));
    }
  }

}
